// ============================================================================
// AFB-0075 / PR 3 — the ordered document list on one packet.
//
// Order is the point of this screen. sort_order is not decoration: it is the
// order the documents are stapled together in, and therefore what decides which
// page of the assembled packet each document's signature boxes land on. So the
// page count sits on every row, next to the arrows that change the order.
//
// Two rules a person can set per row, both stored on the join and neither in
// code, because a new state form is a data change and must not be a deploy:
//
//   applies_to         which worker classes get it. NOTHING TICKED = EVERYONE,
//                      which is the column's own meaning for NULL and is said
//                      in words on the row rather than left to be inferred.
//   applies_to_states  which states get it. Empty = every state. This is what
//                      makes the three withholding forms mutually exclusive.
//
// Only templates that CARRY A DOCUMENT can be added (templateHasDocument, the
// same test the envelope composer uses). A template with no PDF on a packet is
// a document that silently is not in the envelope.
// ============================================================================

import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { EsignTemplate, templateHasDocument } from './envelope-lib';
import {
  PacketDocumentPatch,
  PacketDocumentRow,
  WORKER_CLASSES,
} from './packet-lib';

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';
const SECONDARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-tcs-border px-2 text-[11px] text-tcs-text-secondary disabled:opacity-50';
const ICON_BTN =
  'inline-flex h-7 w-7 items-center justify-center rounded border border-tcs-border text-tcs-text-secondary disabled:opacity-40';

export interface PacketDocumentsProps {
  documents: PacketDocumentRow[];
  /** Active templates. A packet member missing from here is named as a gap. */
  templates: EsignTemplate[];
  /** link_id currently being written, or 'add' while a template is joining. */
  busy: string | null;
  onAdd: (template_id: string) => Promise<void>;
  onSave: (link_id: string, patch: PacketDocumentPatch) => Promise<void>;
  onRemove: (row: PacketDocumentRow) => Promise<void>;
  onMove: (link_id: string, delta: -1 | 1) => Promise<void>;
}

/**
 * "KS, MO" ⇄ ['KS','MO'].
 *
 * Split on anything that is not a letter so "ks mo", "KS/MO" and "KS, MO" all
 * work — a two-letter state code has no punctuation in it, and refusing a
 * separator somebody reasonably typed is a worse failure than accepting four.
 */
function parseStates(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[^A-Za-z]+/)
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s !== ''),
    ),
  );
}

/** What this row's page count is, or why it is not a number. */
function pageLabel(template: EsignTemplate | undefined): string {
  if (!template) return 'template missing';
  if (!template.page_count || template.page_count <= 0) return 'page count unknown';
  return `${template.page_count} page${template.page_count === 1 ? '' : 's'}`;
}

interface RowProps {
  row: PacketDocumentRow;
  template: EsignTemplate | undefined;
  index: number;
  count: number;
  busy: boolean;
  onSave: (patch: PacketDocumentPatch) => void;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
}

function DocumentRow({
  row, template, index, count, busy, onSave, onRemove, onMove,
}: RowProps) {
  const [classes, setClasses] = useState<string[]>(row.applies_to ?? []);
  const [states, setStates] = useState((row.applies_to_states ?? []).join(', '));
  const [required, setRequired] = useState(row.is_required);

  // The parent replaces the row with the re-read one after every save, so the
  // drafts follow what is stored rather than drifting away from it.
  useEffect(() => {
    setClasses(row.applies_to ?? []);
    setStates((row.applies_to_states ?? []).join(', '));
    setRequired(row.is_required);
  }, [row]);

  const parsed = parseStates(states);
  const notCodes = parsed.filter((s) => s.length !== 2);
  const missingPages = !template || !template.page_count || template.page_count <= 0;

  return (
    <li className="rounded-md border border-tcs-border bg-tcs-bg p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-tcs-text">
          <span className="font-medium">{index + 1}. {template?.name ?? row.template_id}</span>{' '}
          <span className={missingPages ? 'text-st-warn-fg' : 'text-tcs-text-muted'}>
            · {pageLabel(template)}
          </span>{' '}
          <span className="text-tcs-text-muted">· {row.template_id}</span>
        </span>
        <span className="flex items-center gap-1">
          <button
            type="button"
            aria-label={`Move ${template?.name ?? row.template_id} earlier`}
            disabled={busy || index === 0}
            onClick={() => onMove(-1)}
            className={ICON_BTN}
          >
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={`Move ${template?.name ?? row.template_id} later`}
            disabled={busy || index === count - 1}
            onClick={() => onMove(1)}
            className={ICON_BTN}
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onRemove}
            className={`${SECONDARY} min-h-[28px]`}
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Remove
          </button>
        </span>
      </div>

      <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
        <div>
          <span className={LABEL}>Who gets it</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {WORKER_CLASSES.map((cls) => (
              <label
                key={cls}
                className="inline-flex items-center gap-1 text-[11px] text-tcs-text-secondary"
              >
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={classes.some((c) => c.toUpperCase() === cls.toUpperCase())}
                  onChange={(e) =>
                    setClasses((cur) =>
                      e.target.checked
                        ? [...cur, cls]
                        : cur.filter((c) => c.toUpperCase() !== cls.toUpperCase()),
                    )
                  }
                />
                {cls}
              </label>
            ))}
          </div>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            {classes.length === 0
              ? 'Nothing ticked — this document goes to everyone.'
              : `Only ${classes.join(', ')}.`}
          </p>
        </div>
        <div>
          <label className={LABEL} htmlFor={`states-${row.link_id}`}>
            Only in these states
          </label>
          <input
            id={`states-${row.link_id}`}
            value={states}
            disabled={busy}
            placeholder="KS, MO — leave blank for every state"
            onChange={(e) => setStates(e.target.value)}
            className={`mt-1 ${INPUT}`}
          />
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            {parsed.length === 0
              ? 'Blank — goes to every state.'
              : `Only ${parsed.join(', ')}. Anyone whose state is unknown gets nothing here.`}
          </p>
          {/* employees.state holds two-letter codes, so "KANSAS" saves without
              complaint and then matches nobody — a state form that quietly
              reaches no one is the failure this whole screen exists to stop. */}
          {notCodes.length > 0 && (
            <p role="alert" className="mt-0.5 text-[11px] text-st-warn-fg">
              {notCodes.join(', ')} — states are stored as two-letter codes, so
              this would match nobody. Use KS, MO, CO.
            </p>
          )}
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1 text-[11px] text-tcs-text-secondary">
          <input
            type="checkbox"
            disabled={busy}
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            onSave({
              applies_to: classes.length > 0 ? classes : null,
              applies_to_states: parsed.length > 0 ? parsed : null,
              is_required: required,
            })
          }
          className={SECONDARY}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                : <Save className="h-3 w-3" aria-hidden />}
          Save rules
        </button>
      </div>
    </li>
  );
}

export function PacketDocuments({
  documents, templates, busy, onAdd, onSave, onRemove, onMove,
}: PacketDocumentsProps) {
  const [toAdd, setToAdd] = useState('');

  const onPacket = new Set(documents.map((d) => d.template_id));
  // Only templates that carry a PDF, and only ones not already here — the
  // UNIQUE on (packet_id, template_id) would refuse the duplicate anyway, but
  // offering it and then failing is a worse way to say the same thing.
  const addable = templates.filter((t) => templateHasDocument(t) && !onPacket.has(t.template_id));

  return (
    <div className="border-t border-tcs-border pt-2">
      <p className="text-[11px] font-semibold text-tcs-text">Documents, in order</p>
      {documents.length === 0 ? (
        <p className="mt-1 text-[11px] italic text-tcs-text-muted">
          No documents on this packet yet. Add the ones everybody gets first —
          the order here is the order they are stapled together in.
        </p>
      ) : (
        <ul className="mt-1.5 space-y-1.5">
          {documents.map((row, i) => (
            <DocumentRow
              key={row.link_id}
              row={row}
              template={templates.find((t) => t.template_id === row.template_id)}
              index={i}
              count={documents.length}
              busy={busy === row.link_id}
              onSave={(patch) => void onSave(row.link_id, patch)}
              onRemove={() => void onRemove(row)}
              onMove={(delta) => void onMove(row.link_id, delta)}
            />
          ))}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <label className={LABEL} htmlFor="packet-add-template">Add a document</label>
          <select
            id="packet-add-template"
            value={toAdd}
            disabled={busy !== null || addable.length === 0}
            onChange={(e) => setToAdd(e.target.value)}
            className={`mt-1 ${INPUT}`}
          >
            <option value="">
              {addable.length === 0
                ? 'No template left that carries a document'
                : 'Choose a template…'}
            </option>
            {addable.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.name}
                {t.page_count && t.page_count > 0
                  ? ` — ${t.page_count} page${t.page_count === 1 ? '' : 's'}`
                  : ' — page count unknown'}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          disabled={busy !== null || toAdd === ''}
          onClick={() => {
            const id = toAdd;
            setToAdd('');
            void onAdd(id);
          }}
          className={SECONDARY}
        >
          {busy === 'add' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          : <Plus className="h-3 w-3" aria-hidden />}
          Add to packet
        </button>
      </div>
      <p className="mt-1 text-[11px] text-tcs-text-muted">
        Only templates that carry a saved PDF can join a packet — a member with
        no document is a page that silently is not in the envelope.
      </p>
    </div>
  );
}
